import { useState, useEffect, useContext } from 'react'
import { useMutate } from 'restful-react'
import { UserContext, UserContextType, UserProfile, createUserProfileFromApiUserDocument } from '../../UserContext'
import useInput, { Conditions, InputBindType } from '../../services/useInput'
import { unstable_batchedUpdates } from 'react-dom'
import * as Constants from '../../services/Constants'
import useRootSnackbar from '../../services/useRootSnackbar'
import { logger } from '../../services/Logger'
import axios from 'axios'
import { env } from '../../services/EnvironmentVariables'
import { persistent } from '../../services/LocalStorage'
import useGdprCompliance from '../../services/useGdprCompliance'
import i18n from 'i18next'
import { Trans, useTranslation } from 'react-i18next'
import ElectronIPC from '../../services/ElectronIPC'
import refererDetailOptions from './constants'
import { MuseLogo } from '@/components/MuseLogo'
import { TextInput } from '@/UIComponents2/TextInput'
import UserIcon from '@/CustomIcons/UserIcon'
import MailIcon from '@/CustomIcons/MailIcon'
import KeyIcon from '@/CustomIcons/KeyIcon'
import EyeClosedIcon from '@/CustomIcons/EyeClosedIcon'
import EyeIcon from '@/CustomIcons/EyeIcon'
import { SimpleSelect } from '@/UIComponents2/Select/SimpleSelect'
import MenuIcon from '@/CustomIcons/MenuIcon'
import { Button } from '@/UIComponents2/Button'
import { useHistory } from 'react-router-dom'
import { motion } from 'framer-motion'
import RightArrowIcon from '@/CustomIcons/RightArrowIcon'
import AlertIcon from '@/CustomIcons/AlertIcon'
import UnauthorizedPageBase from './UnauthorizedPageBase'
import { Checkbox } from '@/UIComponents2/Checkbox'

type GdprCheckboxData = {
    doReceiveAnnouncements?: boolean
    doConfirmPolicy?: boolean
}

function RegisterUserPage() {
    const history = useHistory()
    const { t } = useTranslation()

    const { dispatch } = useContext(UserContext) as UserContextType

    const [generalErrorText, setGeneralErrorText] = useState<string | null>(null)
    const [showGeneralError, setShowGeneralError] = useState<boolean>(false)

    const { formdata, bind, submit, setValue, setError } = useInput(inputModel)

    const { mutate: registerUser, loading: createUserLoading } = useMutate({
        verb: 'POST',
        path: `/v1/users`,
    })

    const RootSnackbar = useRootSnackbar()

    const createAccountHandler = async (args: {
        shouldBeGdprCompliant: boolean
        gdprCheckboxData: GdprCheckboxData
        countryCode: string | null
    }) => {
        const { hasErrors } = await submit()
        if (hasErrors) {
            return
        }

        if (args.shouldBeGdprCompliant && !args.gdprCheckboxData.doConfirmPolicy) {
            RootSnackbar.show({
                severity: 'error',
                message: i18n.t('Please confirm the Privacy Policy and Terms and Conditions checkbox.'),
            })
            return
        }

        try {
            const filteredFormData = {
                ...formdata,
                ...args,
                referer: formdata.referer || undefined,
                refererDetail: formdata.refererDetail || undefined,
            }
            const result = await registerUser(filteredFormData)
            console.log(`registerUser api returned: %O`, result)

            console.log(`set ${Constants.show_welcome_page} to true.`)
            persistent.set(Constants.show_welcome_page, true)

            const { token, user } = result
            const profile: UserProfile = createUserProfileFromApiUserDocument(user)
            await signinSuccessHandler(token, user._id, profile)
        } catch (e) {
            signinErrorHandler(e)
        }
    }

    const signinSuccessHandler = (authToken: string, userUrn: string, profile: UserProfile) => {
        dispatch({
            type: 'login',
            value: { authToken, userUrn, profile, communityProfile: null, isGuest: false },
        })

        RootSnackbar.show({
            message: i18n.t('Created account successfully!'),
            severity: 'success',
        })
    }

    const signinErrorHandler = (e: any) => {
        const code = e?.data?.code
        if (code === 'duplicate') {
            const what = e?.data?.what
            if (what === 'username') {
                setError('username', i18n.t('This username already exists.'))
            } else if (what === 'email') {
                setError('email', i18n.t('This email already exists.'))
            } else {
                setGeneralErrorText(`Unknown error: code=${code} data=${JSON.stringify(e?.data)}`)
            }
        } else if (code === 'invalid_username') {
            const reason = e?.data?.reason
            if (reason === 'too_short') {
                const min = e?.data?.min
                setError('username', `Username must be at least ${min} characters long.`)
            } else if (reason === 'too_long') {
                const max = e?.data?.max
                setError('username', `Username cannot be longer than ${max} characters.`)
            } else if (reason === 'reserved_name') {
                setError('username', `This is a reserved username.`)
            } else {
                setError('username', `Invalid username.`)
            }
        } else if (code === 'invalid_email') {
            setError('email', i18n.t(`We don't think that is a valid email.`))
        } else if (code === 'invalid_password') {
            const reason = e?.data?.reason
            if (reason === 'too_short') {
                const min = e?.data?.min
                setError('password', `Passwords must be at least ${min} characters long.`)
            } else if (reason === 'too_long') {
                setError('username', `Password is too long.`)
            } else {
                setError('username', `Invalid password.`)
            }
        } else if (!e?.data) {
            unstable_batchedUpdates(() => {
                setShowGeneralError(true)
                setGeneralErrorText(
                    i18n.t("Couldn't connect to the server. Please make sure you are connected to the internet.")
                )
            })
        } else {
            console.error(e)
            unstable_batchedUpdates(() => {
                setShowGeneralError(true)
                setGeneralErrorText(`Unknown error: code=${e?.data?.code}`)
            })
        }
    }

    useEffect(() => {
        logger.event({ page: 'register-user', action: 'enter' })
        return () => {
            logger.event({ page: 'register-user', action: 'exit' })
        }
    }, [])

    return (
        <UnauthorizedPageBase loading={createUserLoading}>
            <div className="flex justify-between items-center">
                <div className="w-[108px] h-[24px]">
                    <MuseLogo />
                </div>
                <motion.div
                    initial={{ x: 50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -50, opacity: 0 }}
                    transition={{
                        duration: 0.3,
                        ease: 'easeOut',
                    }}
                    className="flex-none"
                >
                    <Button variant="secondary" onClick={() => history.push('/login')} className="py-0 h-[40px]">
                        {t('Are you a member?')} <span className="text-[#FCE640] pl-[8px]">{t('Log in now')}</span>
                    </Button>
                </motion.div>
            </div>

            <motion.div
                key="register-form"
                className="flex flex-col gap-[48px] justify-center h-[80%]"
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -50, opacity: 0 }}
                transition={{
                    duration: 0.3,
                    ease: 'easeOut',
                }}
            >
                <div className="flex flex-col gap-[14px]">
                    <span className="max-w-[442px] text-[30px]/[30px] font-[500] bg-linear-[98deg,white_21.88%,transparent_139.38%] bg-clip-text text-transparent">
                        {t('Create an account')}
                    </span>
                </div>
                <FormContent
                    bind={bind}
                    onSubmit={createAccountHandler}
                    referer={formdata.referer}
                    setValue={setValue}
                    createUserLoading={createUserLoading}
                    showGeneralError={showGeneralError}
                    generalErrorText={generalErrorText}
                    setShowGeneralError={setShowGeneralError}
                />
            </motion.div>
        </UnauthorizedPageBase>
    )
}

function FormContent({
    bind,
    onSubmit,
    referer,
    setValue,
    createUserLoading,
    showGeneralError,
    generalErrorText,
    setShowGeneralError,
}: {
    bind: InputBindType
    onSubmit: (args: {
        shouldBeGdprCompliant: boolean
        gdprCheckboxData: GdprCheckboxData
        countryCode: string | null
    }) => void
    referer: string | null
    setValue: (key: string, value: string) => void
    createUserLoading: boolean
    showGeneralError: boolean
    generalErrorText: string | null
    setShowGeneralError: (showGeneralError: boolean) => void
}) {
    const { t } = useTranslation()

    const [isPasswordVisible, set_isPasswordVisible] = useState<boolean>(false)

    const [gdprCheckboxData, set_gdprCheckboxData] = useState<GdprCheckboxData>({})

    const [shouldBeGdprCompliant, set_shouldBeGdprCompliant] = useState<boolean | null>(false)

    const { countryCode, queryShouldBeGdprCompliant } = useGdprCompliance()

    useEffect(() => {
        queryShouldBeGdprCompliant().then((b) => {
            set_shouldBeGdprCompliant(!!b)
        })
    }, [])

    const onSubmitHandler = (ev: any) => {
        ev.preventDefault()
        setShowGeneralError(false)

        onSubmit({
            shouldBeGdprCompliant: !!shouldBeGdprCompliant,
            gdprCheckboxData,
            countryCode,
        })
    }

    return (
        <form noValidate onSubmit={onSubmitHandler} className="flex flex-col gap-[12px]">
            {showGeneralError && (
                <div className={'text-black bg-[#FC5050] rounded-[20px] p-[16px] flex items-center gap-[8px]'}>
                    <AlertIcon className="w-[24px] h-[24px]" />
                    {generalErrorText}
                </div>
            )}
            <TextInput
                placeholder={t('Username') || ''}
                {...bind.username}
                error={bind.username.error ? bind.username.helperText : undefined}
                helperText={undefined}
                className="bg-[rgba(255,255,255,0.08)]"
                icon={<UserIcon />}
            />

            <TextInput
                placeholder={t('Email') || ''}
                {...bind.email}
                error={bind.email.error ? bind.email.helperText : undefined}
                helperText={undefined}
                className="bg-[rgba(255,255,255,0.08)]"
                icon={<MailIcon />}
                type="email"
            />

            <TextInput
                placeholder={t('Password') || ''}
                {...bind.password}
                error={bind.password.error ? bind.password.helperText : undefined}
                helperText={undefined}
                className="bg-[rgba(255,255,255,0.08)]"
                icon={<KeyIcon />}
                type={isPasswordVisible ? 'text' : 'password'}
                rightControl={
                    <button
                        className="cursor-pointer opacity-50"
                        onClick={(e) => {
                            e.preventDefault()
                            set_isPasswordVisible(!isPasswordVisible)
                        }}
                    >
                        {isPasswordVisible ? <EyeIcon /> : <EyeClosedIcon />}
                    </button>
                }
            />

            <div className="flex flex-col gap-2.5">
                <div className="flex items-center justify-between leading-[18px]">
                    <span>{t('How did you hear about us?')}</span>
                    <span className="font-[200] opacity-75">{t('Optional')}</span>
                </div>
                <SimpleSelect
                    items={[{ value: 'None', label: 'None' }, ...refererDetailOptions]}
                    value={bind.referer.value}
                    onChange={(value) => {
                        if (value === 'None') {
                            setValue('referer', '')
                            setValue('refererDetail', '')
                        } else {
                            setValue('referer', value)
                            setValue('refererDetail', '')
                        }
                    }}
                    leftElement={<MenuIcon className="h-[20px] w-[20px]" />}
                    widthMatching
                    placeholder={t('Select answer ...') || 'Select answer ...'}
                    triggerClassName="bg-[rgba(255,255,255,0.08)]"
                />
            </div>
            {(referer === 'influencer' || referer === 'other') && (
                <div>
                    {referer === 'influencer' && (
                        <div className="flex flex-col gap-2.5">
                            <div className="flex items-center justify-between leading-[18px]">
                                {t('Whose video did you see?')}
                            </div>
                            <TextInput
                                placeholder={t('Which influencer?') || ''}
                                {...bind.refererDetail}
                                error={bind.refererDetail.error ? bind.refererDetail.helperText : undefined}
                                helperText={undefined}
                                className="bg-[rgba(255,255,255,0.08)]"
                            />
                        </div>
                    )}
                    {referer === 'other' && (
                        <div className="flex flex-col gap-2.5">
                            <div className="flex items-center justify-between leading-[18px]">
                                {t('How did you hear about us?')}
                            </div>
                            <TextInput
                                placeholder={t('How did you hear about us?') || ''}
                                {...bind.refererDetail}
                                error={bind.refererDetail.error ? bind.refererDetail.helperText : undefined}
                                helperText={undefined}
                                className="bg-[rgba(255,255,255,0.08)]"
                            />
                        </div>
                    )}
                </div>
            )}

            <Button
                className="w-full relative"
                variant="primary"
                onClick={onSubmitHandler}
                disabled={createUserLoading}
            >
                {t('Create Account')}
                <RightArrowIcon className="absolute right-4" />
            </Button>

            <div className="w-full flex justify-center">
                {shouldBeGdprCompliant === false && (
                    <>
                        <div className="text-[#B1B1B1] text-center">
                            {/* use <3> tag next b/c I think <Trans> interprets the "and" as the second element. */}
                            <Trans i18nKey={'By signing up, you agree to our <1>Privacy Policy</1> and <3>Terms</3>.'}>
                                By signing up, you agree to our{' '}
                                <a
                                    href={Constants.privacy_policy_link}
                                    className={'text-[#FCE640]'}
                                    onClick={(e) => {
                                        e.preventDefault()
                                        ElectronIPC.openLinkInBrowser(Constants.privacy_policy_link)
                                    }}
                                >
                                    Privacy Policy
                                </a>{' '}
                                and{' '}
                                <a
                                    href={Constants.terms_and_conditions_link}
                                    className="text-[#FCE640]"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        ElectronIPC.openLinkInBrowser(Constants.terms_and_conditions_link)
                                    }}
                                >
                                    Terms
                                </a>
                                .
                            </Trans>
                        </div>
                    </>
                )}
                {shouldBeGdprCompliant === true && (
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-3">
                            <Checkbox
                                checked={!!gdprCheckboxData.doReceiveAnnouncements}
                                onChange={() => {
                                    set_gdprCheckboxData({
                                        ...gdprCheckboxData,
                                        doReceiveAnnouncements: !gdprCheckboxData.doReceiveAnnouncements,
                                    })
                                }}
                            />
                            <div className="">
                                {t(
                                    'I would like to sign up to receive occasional announcements and updates to the platform.'
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Checkbox
                                checked={!!gdprCheckboxData.doConfirmPolicy}
                                onChange={() => {
                                    set_gdprCheckboxData({
                                        ...gdprCheckboxData,
                                        doConfirmPolicy: !gdprCheckboxData.doConfirmPolicy,
                                    })
                                }}
                            />

                            <div className="">
                                <Trans
                                    i18nKey={
                                        'I confirm that I have read and accepted the <1>Privacy Policy</1> and <3>Terms and Conditions</3>.'
                                    }
                                >
                                    I confirm that I have read and accepted the{' '}
                                    <a
                                        href={Constants.privacy_policy_link}
                                        className="text-[#FCE640]"
                                        onClick={(e) => {
                                            e.preventDefault()
                                            ElectronIPC.openLinkInBrowser(Constants.privacy_policy_link)
                                        }}
                                    >
                                        Privacy Policy
                                    </a>{' '}
                                    and{' '}
                                    <a
                                        href={Constants.terms_and_conditions_link}
                                        className="text-[#FCE640]"
                                        onClick={(e) => {
                                            e.preventDefault()
                                            ElectronIPC.openLinkInBrowser(Constants.terms_and_conditions_link)
                                        }}
                                    >
                                        Terms and Conditions
                                    </a>
                                    .
                                </Trans>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </form>
    )
}

// NOTE: the i18n.t() stuff needs to be in a function because the string is dynamically generated.
const inputModel: any = {
    username: {
        errorChecks: [
            {
                trigger: ['onSubmit'],
                condition: Conditions.is_empty,
                message: () => i18n.t('Username is required'),
            },
            {
                trigger: ['onChange', 'onSubmit', 'onBlur'],
                condition: (value: string) => {
                    value = value.trim()
                    // TextEncoder().encode() gets the byte-length of the string.
                    return value.length > 0 && new TextEncoder().encode(value).length < 3
                },
                message: () => i18n.t('Username is too short.'),
            },
            {
                trigger: ['onChange', 'onSubmit', 'onBlur'],
                condition: Conditions.no_contain_spaces,
                message: () => i18n.t('Whitespace characters are not allowed in usernames.'),
            },
            {
                trigger: ['onChange', 'onBlur'],
                condition: async (value: string) => {
                    if (!value) {
                        return false
                    }
                    try {
                        await axios.post(`${env.API_BASE_PATH}/v1/preflight/users`, { username: value })
                    } catch (e) {
                        const ee = (e as any) || {}
                        if (ee.response == null) {
                            return i18n.t('Could not connect to the server.')
                        } else {
                            const code = ee.response?.data?.code
                            if (code === 'duplicate') {
                                return i18n.t('This username is already taken.')
                            } else if (code === 'invalid_username') {
                                const reason = ee.response?.data?.reason
                                if (reason === 'reserved_name') {
                                    return i18n.t('This is a reserved username')
                                } else {
                                    return i18n.t('Not a valid username')
                                }
                            } else {
                                return i18n.t('Not a valid username')
                            }
                        }
                    }
                },
                message: '',
            },
        ],
        errorCheckDebounceMs: 1000,
    },
    email: {
        errorChecks: [
            {
                trigger: ['onSubmit'],
                condition: Conditions.is_empty,
                message: () => i18n.t('Email is required.'),
            },
            {
                trigger: ['onChange', 'onSubmit', 'onBlur'],
                condition: (value: string) => value.trim().length > 0 && value.indexOf('@') < 0,
                message: () => i18n.t('Email must contain the "@" character.'),
            },
            {
                trigger: ['onChange', 'onBlur'],
                condition: async (value: string) => {
                    if (!value) {
                        return false
                    }
                    try {
                        await axios.post(`${env.API_BASE_PATH}/v1/preflight/users`, { email: value })
                    } catch (e) {
                        if ((e as any)?.response?.data?.code === 'duplicate') {
                            return true
                        }
                    }
                },
                message: () => i18n.t('This email is already taken.'),
            },
        ],
        errorCheckDebounceMs: 1000,
    },
    password: {
        errorChecks: [
            {
                trigger: ['onSubmit'],
                condition: Conditions.is_epsilon,
                message: () => i18n.t('Password is required'),
            },
            {
                trigger: ['onSubmit', 'onBlur'],
                condition: (value: string) => value.trim().length > 0 && value.trim().length < 8,
                message: () => i18n.t('Password must be at least 8 characters long.'),
            },
        ],
    },
    referer: {},
    refererDetail: {},
}

export default RegisterUserPage
